import { useApi, useToaster } from "@shopexperts/shared";
import { useQuery } from "@tanstack/vue-query";
import { computed, type MaybeRefOrGetter, toValue, watch } from "vue";

export function planInvitationsQueryKey(expertId: number | null) {
  return ["admin", "experts", expertId, "plan-invitations"] as const;
}

export function useAdminPlanInvitations(expertId: MaybeRefOrGetter<number | null>) {
  const api = useApi();
  const toaster = useToaster();
  const expertIdValue = computed(() => toValue(expertId));

  const query = useQuery({
    queryKey: computed(() => planInvitationsQueryKey(expertIdValue.value)),
    queryFn: () => api.admin.expertBilling.listPlanInvitations({
      params: { expert: expertIdValue.value as number },
    }),
    enabled: computed(() => expertIdValue.value !== null),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    retry: 1,
  });

  watch(query.error, (error) => {
    if (!error) return;
    toaster.create({
      title: "Failed to load payment invitations",
      description: error.message,
      type: "error",
    });
    console.error("[useAdminPlanInvitations] Error:", error);
  });

  return {
    invitations: computed(() => query.data.value ?? []),
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}

export default useAdminPlanInvitations;
